import { FunctionComponent, useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import Button from "../../../../components/Button";
import { useAppHandlers } from "../../../../services/actions/app/useAppHandlers";
import Row from "../../../../components/Row";
import Table from "../../../../components/Table";
import Text from "../../../../components/Text";
import Checkbox from "../../../../components/Checkbox";
import SelectAllCheckbox from "../../../../components/SelectAllCheckbox";
import moment from "moment";
import { useConnectionsHandlers } from "../../../../services/actions/connections/useConnectionsHandlers";
import EmptyPage from "../../../../components/EmptyPage";
import { useConnectionsStore } from "../../../../stores/connections/useConnectionsStore";
import EditDeleteActionDropdown from "../../../../fragments/dropdowns/EditDeleteActionDropdown";
import { styles } from "../../../../styles/themes/style";
import { IWorkspaceCustomField } from "../../../../interfaces/models/workspace";

interface ICustomFieldsProps {}

const CustomFields: FunctionComponent<ICustomFieldsProps> = () => {
  const { setCreateCustomFieldModal, hideModal, setDestructiveModal } =
    useAppHandlers();
  const {
    getCustomFieldDefinitionsHandler,
    deleteCustomFieldDefinitionsHandler,
  } = useConnectionsHandlers();
  const { store: { customFieldDefinitions } } = useConnectionsStore();

  const [selectedFieldKeys, setSelectedFieldKeys] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCustomFields();
  }, []);

   const fetchCustomFields = useCallback(async () => {
     setLoading(true);
     try {
       await getCustomFieldDefinitionsHandler();
     } finally {
       setLoading(false);
     }
   }, [getCustomFieldDefinitionsHandler]);

   const handleCreateField = () => {
     setCreateCustomFieldModal(() => {
       hideModal();
     });
   };

  const handleDeleteFields = async () => {
    if (selectedFieldKeys.length === 0) return;

    setDestructiveModal({
      headingText: "Delete custom fields",
      descriptionText: `Are you sure you want to delete ${
        selectedFieldKeys.length
      } field${
        selectedFieldKeys.length > 1 ? "s" : ""
      }? This action cannot be undone.`,
      onCancelHandler: () => hideModal(),
      onConfirmHandler: async () => {
        hideModal();
        try {
          await deleteCustomFieldDefinitionsHandler(selectedFieldKeys);
          setSelectedFieldKeys([]);
          setIsAllSelected(false);
        } catch (error) {
          console.error("Error deleting custom fields:", error);
        }
      },
    });
  };

   const selectAllCheckboxHandler = () => {
     if (isAllSelected || selectedFieldKeys.length === customFieldDefinitions.length) {
       setSelectedFieldKeys([]);
       setIsAllSelected(false);
     } else {
       setSelectedFieldKeys(customFieldDefinitions.map((field: IWorkspaceCustomField) => field.key));
       setIsAllSelected(true);
     }
   };

   const selectOnThisPageHandler = () => {
     const fieldKeysOnPage = customFieldDefinitions.map((field: IWorkspaceCustomField) => field.key);
     const newSelection = [...selectedFieldKeys, ...fieldKeysOnPage.filter((key: string) => !selectedFieldKeys.includes(key))];
     setSelectedFieldKeys(newSelection);
     if (newSelection.length === customFieldDefinitions.length) {
       setIsAllSelected(true);
     }
   };

   const clearHandler = () => {
     setSelectedFieldKeys([]);
     setIsAllSelected(false);
   };

  const handleSelectField = (key: string) => {
    if (selectedFieldKeys.includes(key)) {
      const updated = selectedFieldKeys.filter((k) => k !== key);
      setSelectedFieldKeys(updated);
      setIsAllSelected(false);
    } else {
      const updated = [...selectedFieldKeys, key];
      setSelectedFieldKeys(updated);
      if (updated.length === customFieldDefinitions.length) {
        setIsAllSelected(true);
      }
    }
  };

   const handleDeleteSingleField = (field: any) => {
     setDestructiveModal({
       headingText: "Delete custom field",
       descriptionText:
         "Are you sure you want to delete this field? This action cannot be undone.",
       onCancelHandler: () => hideModal(),
       onConfirmHandler: async () => {
         hideModal();
         try {
           await deleteCustomFieldDefinitionsHandler([field.key]);
           setSelectedFieldKeys(selectedFieldKeys.filter((k) => k !== field.key));
         } catch (error) {
           console.error("Error deleting custom field:", error);
         }
       },
     });
   };

   const handleEditField = (field: any) => {
     setCreateCustomFieldModal(() => {
       hideModal();
     }, {
       key: field.key,
       label: field.label,
       type: field.type
     });
   };

  const fieldTypeLabels: any = {
    single_line_text: "Single-line text",
    rich_text: "Rich text",
    number: "Number",
    date: "Date",
    email: "Email",
    url: "URL",
    phone: "Phone",
    user: "Member",
  };

   const columns = [
     {
       header: customFieldDefinitions.length > 0 ? (
         <SelectAllCheckbox
           isCheckboxChecked={
             isAllSelected ||
             (selectedFieldKeys.length > 0 &&
               selectedFieldKeys.length === customFieldDefinitions.length)
           }
           pageSize={customFieldDefinitions.length}
           total={customFieldDefinitions.length}
           paginated={customFieldDefinitions.length}
           isAllSelected={isAllSelected}
           selected={selectedFieldKeys.length}
           label={"fields"}
           selectAllHandler={selectAllCheckboxHandler}
           selectOnThisPageHadler={selectOnThisPageHandler}
           clearHandler={clearHandler}
           backgroundColor="white"
         />
       ) : "",
       key: ["checkbox"],
       show: true,
       width: "28px",
       showSmall: true,
     },
     {
       header: "Name",
       key: ["label"],
       show: true,
       width: "35%",
       showSmall: true,
     },
     {
       header: "Type",
       key: ["type"],
       show: true,
       width: "25%",
       showSmall: true,
     },
     {
       header: "Created",
       key: ["createdAt"],
       show: true,
       width: "25%",
     },
     {
       header: "",
       key: ["actions"],
       show: true,
       width: "15%",
     },
   ];

   return (
     <Container>
       <ControlBar>
         <Row gap="12px" alignItems>
           <HeaderTitle>Contact properties</HeaderTitle>
         </Row>
         <Row gap="12px" alignItems>
            <Button
              $type="red"
              $width="123px"
              $SVGtype="trashCan"
              $iconColor={styles.colors.white}
              onClickHandler={handleDeleteFields}
              disabled={selectedFieldKeys.length === 0}
            >
              Delete fields
            </Button>
           <Button
             $type="blue"
             $width="148px"
             $SVGtype="plus"
             onClickHandler={handleCreateField}
           >
             Create new field
           </Button>
         </Row>
       </ControlBar>

       <TableWrapper>
        <Table
          pageLoading={loading ? 7 : undefined}
          columns={columns}
          $noGap={true}
          noDataPage={
            <EmptyPage
              svgType="importEmpty"
              title="No Custom Fields"
              label="Create your first custom field to extend your contact data"
            />
          }
          data={customFieldDefinitions?.map((field: any) => ({
            object: { ...field, borderTop: "#e0e0e0 1px solid" },
            checkbox: (
              <Checkbox
                checkboxId={field.key}
                checked={selectedFieldKeys.includes(field.key)}
                onClickHandler={() => handleSelectField(field.key)}
              />
            ),
            label: <FieldName>{field.label}</FieldName>,
            type: <Text>{fieldTypeLabels[field.type] || field.type}</Text>,
            createdAt: (
              <Text>
                {moment(field.createdAt).format("MMM D, YYYY [at] HH:mm")}
              </Text>
            ),
             actions: (
               <ActionsCell>
                 <EditDeleteActionDropdown
                   object={field}
                   type="field"
                   title="Edit"
                   deleteTitle="Delete"
                   onEditHandler={handleEditField}
                   onDeleteHandler={handleDeleteSingleField}
                 />
               </ActionsCell>
             ),
          }))}
        />
      </TableWrapper>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 574px;
  max-height: 574px;
`;

const ControlBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTitle = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  color: ${styles.colors.black600};
`;

const TableWrapper = styled.div`
  width: 100%;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0; /* Important for flex child to be scrollable */

  /* Custom row height for custom fields table */
  .react-data-grid {
    .rdg-row {
      height: 48px !important;
    }
  }
`;

const FieldName = styled(Text)`
  font-weight: 600;
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
`;
export default CustomFields;
